"use client";

import { Capacitor } from "@capacitor/core";
import {
  Check,
  CircleFadingPlus,
  Link,
  Loader2,
  QrCode,
  Send,
  Share,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import type { StoryShareEntity } from "@/db/schema/story";
import { useStoryViewer } from "@/features/stories/components/story-viewer/story-viewer-provider";
import {
  type ShareEntityType,
  useForwardMessages,
  useShareToChat,
  useUserChats,
} from "@/hooks/use-chat";
import { useStoryShareDraftMutation } from "@/hooks/use-feed-share";
import { useShortShareUrl } from "@/hooks/use-short-share-url";
import { buildShareUrl, type ShareSurface } from "@/lib/analytics/share-link";
import { chatHaptics } from "@/lib/chat-haptics";
import type { MusicAttachment } from "@/lib/music/attachment";
import {
  downloadShareQr,
  type ShareQrRef,
} from "@/lib/share-qr/download.client";
import { cn } from "@/lib/utils";
import { ChatTargetPicker, useChatTargetSelection } from "./chat-target-picker";
import {
  GLASS_FIELD,
  GLASS_PRIMARY_BUTTON,
  GLASS_SHEET,
  GLASS_SHEET_OVERLAY,
  GLASS_TILE,
} from "./liquid-glass-sheet";
import { useSendLinkToChats } from "./use-send-link-to-chats";
import { useSendSongToChats } from "./use-send-song-to-chats";

/** Entity kinds that can also be reshared to a story (subset of the story
 * share resolver's supported types). */
const STORY_SHAREABLE_TYPES = new Set<ShareEntityType>([
  "post",
  "activity_post",
  "activity_session",
  "profile",
  "hiive",
  "event",
  "venue",
  "movement_activity",
  "movement_route",
  "weight_training_workout",
  "weight_training_routine",
]);

function toStoryShareEntity(
  entityType: ShareEntityType,
): StoryShareEntity | null {
  return STORY_SHAREABLE_TYPES.has(entityType)
    ? (entityType as StoryShareEntity)
    : null;
}

/**
 * Entity type to the label the funnel shows. Kept beside the payload type so a
 * new shareable entity is one line here rather than an untracked `other`.
 */
const SURFACE_BY_ENTITY: Partial<Record<ShareEntityType, ShareSurface>> = {
  event: "event_page",
  hiive: "hiive_page",
  venue: "venue_page",
  profile: "profile_page",
  post: "post",
  story: "story",
};

export type SendToChatPayload =
  | { type: "entity"; entityType: ShareEntityType; entityId: string }
  | { type: "forward"; messageIds: number[] }
  /**
   * A shareable page with no share card of its own (an event's menu): the
   * same sheet, sent to chats as a link message that unfurls. `url` is the
   * page; the outgoing link is built from it like any other share.
   */
  | { type: "link"; url: string }
  /** A song, sent as a playable song card. */
  | { type: "song"; song: MusicAttachment };

export type NativeSharePayload = {
  title?: string;
  text?: string;
  url?: string;
  /**
   * Which surface this share was opened from, stamped onto the outgoing link.
   *
   * Optional, and the entity type is the fallback, so an existing caller keeps
   * working — but naming it here is what lets "Where they came from" tell a
   * share of the event page from a share of a story about it.
   */
  surface?: ShareSurface;
};

interface SendToChatDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: SendToChatPayload | null;
  nativeShare?: NativeSharePayload | null;
  onShared?: (target: "chat" | "story" | "system" | "copy") => void;
  /**
   * What the "Download QR" action prints. Hiives, events and venues are
   * derived from the entity payload; anything else (an experience) passes it.
   */
  qr?: ShareQrRef | null;
}

/** Entity shares that get a printable QR without the caller asking. */
const QR_ENTITY_KINDS = new Set<ShareEntityType>(["hiive", "event", "venue"]);

/**
 * `navigator.clipboard` is missing outside a secure context (a phone hitting
 * the dev server over the LAN) and can reject when the document lacks focus
 * or permission, so fall back to the old selection copy rather than telling
 * someone their link could not be copied.
 */
async function copyToClipboard(text: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to the selection copy.
    }
  }
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  // 16px so focusing it can never trigger the iOS input zoom.
  field.style.fontSize = "16px";
  // Inside the open sheet, so the drawer's focus trap doesn't pull focus
  // back before the selection is copied.
  const host =
    document.activeElement?.closest('[data-slot="drawer-content"]') ??
    document.body;
  host.appendChild(field);
  field.select();
  const ok = document.execCommand("copy");
  field.remove();
  if (!ok) throw new Error("Copy failed");
}

/** Closing the share sheet is not an error, on the web or in the app shell. */
function isShareCancel(error: unknown) {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return (
    error instanceof Error &&
    /abort|cancel/i.test(`${error.name} ${error.message}`)
  );
}

/** Whether "Share…" can open a real system sheet here. */
function canOpenSystemShareSheet() {
  if (Capacitor.isNativePlatform()) return true;
  return (
    typeof navigator !== "undefined" && typeof navigator.share === "function"
  );
}

/**
 * Universal "Send to" sheet — used by entity pages ("Send in chat") and by
 * message forwarding. Targets are the user's existing conversations.
 */
export function SendToChatDrawer({
  open,
  onOpenChange,
  payload,
  nativeShare,
  onShared,
  qr,
}: SendToChatDrawerProps) {
  const router = useRouter();
  const qrRef: ShareQrRef | null =
    qr ??
    (payload?.type === "entity" &&
    QR_ENTITY_KINDS.has(payload.entityType) &&
    Number(payload.entityId) > 0
      ? {
          kind: payload.entityType as ShareQrRef["kind"],
          id: Number(payload.entityId),
        }
      : null);
  const [qrDownloading, setQrDownloading] = useState(false);
  const handleDownloadQr = async () => {
    if (!qrRef || qrDownloading) return;
    setQrDownloading(true);
    try {
      await downloadShareQr(qrRef);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Couldn't download the QR code",
      );
    } finally {
      setQrDownloading(false);
    }
  };
  const { data: chats = [], isLoading } = useUserChats("all", {
    enabled: open,
  });
  const shareMutation = useShareToChat();
  const forwardMutation = useForwardMessages();
  const sendLinkMutation = useSendLinkToChats();
  const sendSongMutation = useSendSongToChats();
  const storyShareDraftMutation = useStoryShareDraftMutation();
  const { openComposer } = useStoryViewer();

  const [query, setQuery] = useState("");
  const { selectedIds, toggleChat, clearSelection } = useChatTargetSelection();
  const [comment, setComment] = useState("");

  const isForward = payload?.type === "forward";
  const isLink = payload?.type === "link";
  const isSending =
    shareMutation.isPending ||
    forwardMutation.isPending ||
    sendLinkMutation.isPending ||
    sendSongMutation.isPending;
  const storyShareEntityType =
    payload?.type === "entity" ? toStoryShareEntity(payload.entityType) : null;
  // `Boolean(...)` and not the bare `nativeShare`: this feeds the dependency
  // array of the pre-warm effect below, and `nativeShare` is an object literal
  // at most call sites — so an object-identity dependency would re-run the
  // effect on every render of the parent, which is exactly what the comment
  // there claims it avoids.
  const canNativeShare =
    (payload?.type === "entity" && Boolean(nativeShare)) || isLink;
  const pageUrl =
    nativeShare?.url ?? (payload?.type === "link" ? payload.url : undefined);

  /**
   * The outgoing link, resolved on OPEN rather than on tap.
   *
   * `navigator.share()` must be called in the same task as the gesture that
   * triggered it, so there is no room to await a shortening request inside
   * `handleNativeShare` — iOS Safari would refuse the sheet. Resolving here
   * means the handler reads a value that has been sitting ready since the
   * drawer opened, and falls back to the long URL if it never arrived.
   *
   * Depends on the primitive fields rather than `nativeShare` itself: the prop
   * is an object literal at most call sites, so a `nativeShare` dependency
   * would re-run this on every render of the parent.
   */
  const shareSurface =
    nativeShare?.surface ??
    (payload?.type === "entity"
      ? SURFACE_BY_ENTITY[payload.entityType]
      : undefined);
  const [longShareUrl, setLongShareUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !canNativeShare) {
      setLongShareUrl(null);
      return;
    }
    const raw =
      pageUrl ??
      (typeof window !== "undefined" ? window.location.href : undefined);
    setLongShareUrl(raw ? buildShareUrl(raw, { surface: shareSurface }) : null);
  }, [open, canNativeShare, pageUrl, shareSurface]);

  const shortShareUrl = useShortShareUrl(longShareUrl);

  const reset = () => {
    setQuery("");
    clearSelection();
    setComment("");
    setCopied(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleAddToStory = async () => {
    if (payload?.type !== "entity" || !storyShareEntityType) return;
    try {
      const draft = await storyShareDraftMutation.mutateAsync({
        entityType: storyShareEntityType,
        entityId: payload.entityId,
      });
      onShared?.("story");
      handleOpenChange(false);
      openComposer({ sharedEntity: draft.sharedEntity });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't prepare this story",
      );
    }
  };

  /**
   * The link that leaves the app: the short form when it resolved in time,
   * the full one when it did not.
   *
   * Not `window.location.href` verbatim, which is what this used to hand out.
   * The current URL carries the sharer's own session state — `?from=%2Fhiives`
   * is a back-button breadcrumb — and carries nothing that says the link came
   * from a share. `buildShareUrl` strips the first and adds the second, which
   * is the entire reason organic shares were previously indistinguishable
   * from direct traffic in the queen funnel.
   *
   * Synchronous on purpose: `navigator.share()` must run in the same task as
   * the tap, so nothing here may await.
   */
  const resolveOutgoingUrl = (): string | undefined => {
    const rawUrl =
      pageUrl ??
      (typeof window !== "undefined" ? window.location.href : undefined);
    const fullUrl =
      longShareUrl ??
      (rawUrl ? buildShareUrl(rawUrl, { surface: shareSurface }) : undefined);
    return shortShareUrl ?? fullUrl;
  };

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopyLink = async () => {
    const url = resolveOutgoingUrl();
    if (!url) {
      toast.error("Couldn't copy the link");
      return;
    }
    try {
      await copyToClipboard(url);
      chatHaptics.copied();
      setCopied(true);
      toast.success("Link copied");
      onShared?.("copy");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const handleNativeShare = async () => {
    if (!canNativeShare) return;
    const url = resolveOutgoingUrl();
    const shareData: ShareData = {};
    if (nativeShare?.title) shareData.title = nativeShare.title;
    if (nativeShare?.text) shareData.text = nativeShare.text;
    if (url) shareData.url = url;

    // The app shell: the Capacitor plugin opens the real iOS/Android sheet and
    // has no user-activation rule, so the dynamic import may await first.
    if (Capacitor.isNativePlatform()) {
      try {
        const { Share } = await import("@capacitor/share");
        await Share.share({
          title: shareData.title,
          text: shareData.text,
          url: shareData.url,
          dialogTitle: "Share",
        });
        onShared?.("system");
        handleOpenChange(false);
        return;
      } catch (error) {
        if (isShareCancel(error)) return;
        // Fall through to the web path rather than dead-ending the tap.
      }
    }

    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
        onShared?.("system");
        handleOpenChange(false);
        return;
      }

      if (url) {
        await copyToClipboard(url);
        chatHaptics.copied();
        onShared?.("copy");
        toast.success("Link copied");
        handleOpenChange(false);
        return;
      }

      toast.error("Sharing isn't available here");
    } catch (error) {
      if (isShareCancel(error)) return;
      toast.error(error instanceof Error ? error.message : "Couldn't share");
    }
  };

  const handleSend = async () => {
    if (!payload || selectedIds.length === 0) return;
    try {
      if (payload.type === "link") {
        const url = resolveOutgoingUrl();
        if (!url) throw new Error("Couldn't send the link");
        await sendLinkMutation.mutateAsync({
          url,
          chatIds: selectedIds,
          comment: comment.trim() || undefined,
        });
      } else if (payload.type === "song") {
        await sendSongMutation.mutateAsync({
          song: payload.song,
          chatIds: selectedIds,
          comment: comment.trim() || undefined,
        });
      } else if (payload.type === "entity") {
        await shareMutation.mutateAsync({
          entityType: payload.entityType,
          entityId: payload.entityId,
          chatIds: selectedIds,
          comment: comment.trim() || undefined,
        });
      } else {
        await forwardMutation.mutateAsync({
          messageIds: payload.messageIds,
          targetChatIds: selectedIds,
        });
      }
      chatHaptics.forwarded();
      const destinationChatId =
        selectedIds.length === 1 ? selectedIds[0] : null;
      toast.success(
        destinationChatId
          ? "Sent to chat"
          : `Sent to ${selectedIds.length} chats`,
        destinationChatId
          ? {
              action: {
                label: "Open chat",
                onClick: () => router.push(`/chat/${destinationChatId}`),
              },
            }
          : undefined,
      );
      if (payload.type !== "forward") onShared?.("chat");
      handleOpenChange(false);
    } catch (error) {
      chatHaptics.sendFailed();
      toast.error(error instanceof Error ? error.message : "Failed to send");
    }
  };

  const showQuickActions =
    (payload?.type === "entity" || payload?.type === "link") && !query;
  const systemShareAvailable =
    open && canNativeShare && canOpenSystemShareSheet();
  const quickActionCount =
    Number(systemShareAvailable) +
    Number(canNativeShare) +
    Number(Boolean(qrRef));

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} repositionInputs>
      <DrawerContent
        className={cn(GLASS_SHEET, "z-[1200] max-h-[88vh]")}
        overlayClassName={cn(GLASS_SHEET_OVERLAY, "z-[1190]")}
      >
        <div className="px-4 pt-2.5 pb-3 text-center">
          <DrawerTitle className="text-[17px] font-semibold tracking-tight text-text-primary dark:text-white">
            {isForward ? "Forward" : "Share"}
          </DrawerTitle>
        </div>

        {showQuickActions && quickActionCount > 0 ? (
          <div
            className={cn(
              "grid shrink-0 gap-2 px-4",
              quickActionCount === 3
                ? "grid-cols-3"
                : quickActionCount === 2
                  ? "grid-cols-2"
                  : "grid-cols-1",
              storyShareEntityType ? "pb-2" : "pb-4",
            )}
          >
            {systemShareAvailable ? (
              <ShareQuickAction
                label="Share"
                ariaLabel="Share with another app"
                icon={<Share />}
                onClick={() => void handleNativeShare()}
              />
            ) : null}
            {canNativeShare ? (
              <ShareQuickAction
                label={copied ? "Copied" : "Copy"}
                ariaLabel={copied ? "Link copied" : "Copy link"}
                icon={copied ? <Check className="text-success" /> : <Link />}
                onClick={() => void handleCopyLink()}
              />
            ) : null}
            {qrRef ? (
              <ShareQuickAction
                label="QR"
                ariaLabel="Save QR code"
                icon={
                  qrDownloading ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <QrCode />
                  )
                }
                disabled={qrDownloading}
                onClick={() => void handleDownloadQr()}
              />
            ) : null}
          </div>
        ) : null}

        {showQuickActions && storyShareEntityType ? (
          <div className="shrink-0 px-4 pb-4">
            <ShareWideAction
              label="Add to story"
              icon={
                storyShareDraftMutation.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <CircleFadingPlus />
                )
              }
              disabled={storyShareDraftMutation.isPending}
              onClick={() => void handleAddToStory()}
            />
          </div>
        ) : null}

        <ChatTargetPicker
          chats={chats}
          isLoading={isLoading}
          query={query}
          onQueryChange={setQuery}
          selectedIds={selectedIds}
          onToggle={toggleChat}
        />

        <AnimatePresence>
          {selectedIds.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ type: "spring", stiffness: 420, damping: 34 }}
              className="shrink-0 space-y-2 border-t border-black/[0.06] bg-white/50 px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] dark:border-white/[0.08] dark:bg-black/20"
            >
              {!isForward ? (
                <input
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Add a message…"
                  aria-label="Message"
                  maxLength={2000}
                  className={cn(
                    "h-11 w-full rounded-full px-4 text-base text-text-primary placeholder:text-text-tertiary focus:outline-none md:text-sm dark:text-white dark:placeholder:text-white/40",
                    GLASS_FIELD,
                  )}
                />
              ) : null}
              <button
                type="button"
                disabled={isSending}
                onClick={() => void handleSend()}
                className={GLASS_PRIMARY_BUTTON}
              >
                {isSending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                {selectedIds.length === 1
                  ? "Send"
                  : `Send to ${selectedIds.length} chats`}
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </DrawerContent>
    </Drawer>
  );
}

function ShareQuickAction({
  label,
  icon,
  onClick,
  disabled,
  ariaLabel,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        GLASS_TILE,
        "flex h-[4.5rem] min-w-0 flex-col items-center justify-center gap-1.5 rounded-2xl px-2",
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-6 items-center justify-center [&_svg]:size-[22px]"
      >
        {icon}
      </span>
      <span
        aria-live="polite"
        className="max-w-full truncate text-[13px] leading-none font-medium"
      >
        {label}
      </span>
    </button>
  );
}

/** Full-width secondary action, sits under the primary quick-action row. */
function ShareWideAction({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        GLASS_TILE,
        "flex h-11 w-full items-center justify-center gap-2 rounded-2xl px-3",
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-5 items-center justify-center [&_svg]:size-[18px]"
      >
        {icon}
      </span>
      <span className="text-[14px] leading-none font-medium">{label}</span>
    </button>
  );
}
